/**
 * GameBoard - Konva.js canvas rendering for game board
 * Renders the 3x3 grid, pieces, and animations
 * Based on ADR-001: Technology Stack Selection
 */

import Konva from 'konva';
import type { GameState, Player } from '@/types';
import { GAME_CONSTANTS } from '@/types';

interface BoardConfig {
  container: string;
  width: number;
  height: number;
  onCellClick: (row: number, col: number) => void;
}

/**
 * GameBoard
 * Handles Konva.js canvas rendering and interactions
 */
export class GameBoard {
  private stage: Konva.Stage;
  private layer: Konva.Layer;
  private cellSize: number;
  private padding: number;
  private cells: Konva.Rect[][] = [];
  private pieces: (Konva.Group | null)[][] = [];
  private onCellClick: (row: number, col: number) => void;

  constructor(config: BoardConfig) {
    const container = document.getElementById(config.container);
    if (!container) {
      throw new Error(`Container element '${config.container}' not found`);
    }

    this.onCellClick = config.onCellClick;
    
    // Calculate dimensions
    const size = Math.min(config.width, config.height);
    this.padding = size * 0.05;
    this.cellSize = (size - this.padding * 2) / GAME_CONSTANTS.BOARD_SIZE;

    // Create stage and layer
    this.stage = new Konva.Stage({
      container: config.container,
      width: size,
      height: size,
    });

    this.layer = new Konva.Layer();
    this.stage.add(this.layer);

    // Initialize board
    this.createGrid();
    this.layer.draw();
  }

  /**
   * Create the 3x3 grid
   */
  private createGrid(): void {
    const gridColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-border-strong')
      .trim();

    for (let row = 0; row < GAME_CONSTANTS.BOARD_SIZE; row++) {
      this.cells[row] = [];
      this.pieces[row] = [];

      for (let col = 0; col < GAME_CONSTANTS.BOARD_SIZE; col++) {
        const x = this.padding + col * this.cellSize;
        const y = this.padding + row * this.cellSize;

        // Create cell rectangle
        const cell = new Konva.Rect({
          x,
          y,
          width: this.cellSize,
          height: this.cellSize,
          stroke: gridColor,
          strokeWidth: 3,
          listening: true,
        });

        // Add hover effect
        cell.on('mouseenter', () => {
          cell.fill(getComputedStyle(document.documentElement)
            .getPropertyValue('--color-hover')
            .trim());
          this.layer.draw();
        });

        cell.on('mouseleave', () => {
          cell.fill('transparent');
          this.layer.draw();
        });

        // Add click handler
        cell.on('click tap', () => {
          this.onCellClick(row, col);
        });

        const rowCells = this.cells[row];
        const rowPieces = this.pieces[row];
        if (rowCells && rowPieces) {
          rowCells[col] = cell;
          rowPieces[col] = null;
        }
        this.layer.add(cell);
      }
    }
  }

  /**
   * Render game state
   */
  public render(state: GameState): void {
    // Clear all pieces
    for (let row = 0; row < GAME_CONSTANTS.BOARD_SIZE; row++) {
      for (let col = 0; col < GAME_CONSTANTS.BOARD_SIZE; col++) {
        const piece = this.pieces[row]?.[col];
        if (piece) {
          piece.destroy();
          if (this.pieces[row]) {
            this.pieces[row]![col] = null;
          }
        }
      }
    }

    // Render pieces
    for (let row = 0; row < GAME_CONSTANTS.BOARD_SIZE; row++) {
      for (let col = 0; col < GAME_CONSTANTS.BOARD_SIZE; col++) {
        const cellValue = state.board[row]?.[col];
        if (cellValue) {
          this.drawPiece(row, col, cellValue);
        }
      }
    }

    // Highlight winning cells
    if (state.winningCells && state.winningCells.length > 0) {
      this.highlightWinningCells(state.winningCells);
    }

    this.layer.draw();
  }

  /**
   * Draw X or O piece in cell
   */
  private drawPiece(row: number, col: number, player: Player): void {
    const x = this.padding + col * this.cellSize;
    const y = this.padding + row * this.cellSize;
    const centerX = x + this.cellSize / 2;
    const centerY = y + this.cellSize / 2;
    const pieceSize = this.cellSize * 0.35;

    const color = player === 'X'
      ? getComputedStyle(document.documentElement).getPropertyValue('--color-player-x').trim()
      : getComputedStyle(document.documentElement).getPropertyValue('--color-player-o').trim();

    const group = new Konva.Group({ opacity: 0 });

    if (player === 'X') {
      // Draw X (two diagonal lines)
      const line1 = new Konva.Line({
        points: [
          centerX - pieceSize, centerY - pieceSize,
          centerX + pieceSize, centerY + pieceSize,
        ],
        stroke: color,
        strokeWidth: 8,
        lineCap: 'round',
      });

      const line2 = new Konva.Line({
        points: [
          centerX + pieceSize, centerY - pieceSize,
          centerX - pieceSize, centerY + pieceSize,
        ],
        stroke: color,
        strokeWidth: 8,
        lineCap: 'round',
      });

      group.add(line1);
      group.add(line2);
    } else {
      // Draw O (circle)
      const circle = new Konva.Circle({
        x: centerX,
        y: centerY,
        radius: pieceSize,
        stroke: color,
        strokeWidth: 8,
      });

      group.add(circle);
    }

    this.layer.add(group);
    const rowPieces = this.pieces[row];
    if (rowPieces) {
      rowPieces[col] = group;
    }

    // Animate piece appearance
    group.to({
      opacity: 1,
      duration: 0.3,
      easing: Konva.Easings.EaseOut as unknown as (t: number) => number,
    });
  }

  /**
   * Highlight winning cells
   */
  private highlightWinningCells(cellIndices: number[]): void {
    const highlightColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-win-highlight')
      .trim();

    cellIndices.forEach((index) => {
      const row = Math.floor(index / GAME_CONSTANTS.BOARD_SIZE);
      const col = index % GAME_CONSTANTS.BOARD_SIZE;
      const cell = this.cells[row]?.[col];
      
      if (cell) {
        cell.fill(highlightColor + '40'); // Add transparency
        cell.to({
          opacity: 0.7,
          duration: 0.5,
          easing: Konva.Easings.EaseInOut as unknown as (t: number) => number,
        });
      }
    });
  }

  /**
   * Clear the board
   */
  public clear(): void {
    for (let row = 0; row < GAME_CONSTANTS.BOARD_SIZE; row++) {
      for (let col = 0; col < GAME_CONSTANTS.BOARD_SIZE; col++) {
        const piece = this.pieces[row]?.[col];
        if (piece) {
          piece.destroy();
          const rowPieces = this.pieces[row];
          if (rowPieces) {
            rowPieces[col] = null;
          }
        }

        const cell = this.cells[row]?.[col];
        if (cell) {
          cell.fill('transparent');
        }
      }
    }
    this.layer.draw();
  }

  /**
   * Resize the board (for responsive design)
   */
  public resize(width: number, height: number): void {
    const size = Math.min(width, height);
    this.stage.width(size);
    this.stage.height(size);
    this.cellSize = (size - this.padding * 2) / GAME_CONSTANTS.BOARD_SIZE;
    
    // Recreate grid with new size
    this.layer.destroyChildren();
    this.cells = [];
    this.pieces = [];
    this.createGrid();
    this.layer.draw();
  }

  /**
   * Destroy the board and cleanup
   */
  public destroy(): void {
    this.stage.destroy();
  }
}
